import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import Input from '../../components/ui/Input';
import Modal from '../../components/ui/Modal';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import StatCard from '../../components/ui/StatCard';
import GradientIcon from '../../components/ui/GradientIcon';
import { Users, CheckCircle2, Calendar, Star } from 'lucide-react';
import Pagination from '../../components/ui/Pagination';
import { clientService } from '../../services';
import { notifications } from '../../utils/toast';

/**
 * Trainers Management Page (Client)
 * Manage trainers within the organization
 */

const TrainersPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [trainers, setTrainers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [selectedTrainer, setSelectedTrainer] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showActionMenu, setShowActionMenu] = useState(false);
  const [showStatusMenu, setShowStatusMenu] = useState(false);
  const [newTrainerData, setNewTrainerData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    role: 'Trainer',
    status: 'active',
    bio: '',
    expertise: [],
    expertiseInput: '',
  });
  const [editTrainerData, setEditTrainerData] = useState({
    id: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    role: 'Trainer',
    expertise: [],
    expertiseInput: '',
    bio: '',
    status: 'active',
  });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [trainerToDelete, setTrainerToDelete] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [addonLoading, setAddonLoading] = useState(false);
  const itemsPerPage = 10;

  // Organization plan from API
  const [organizationPlan, setOrganizationPlan] = useState({
    plan: 'Free',
    trainersLimit: 2,
    trainersUsed: 0,
    isUnlimited: false,
  });

  useEffect(() => {
    // Close dropdown when clicking outside
    const handleClickOutside = (event) => {
      if (!event.target.closest('.dropdown-container')) {
        setShowActionMenu(false);
        setShowStatusMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    fetchTrainers();
  }, [location.key]);

  const fetchTrainers = async () => {
    try {
      setLoading(true);
      const response = await clientService.getTrainers({ status: 'all' });
      if (response.success) {
        // Transform API response to match component format
        const transformedTrainers = (response.data.trainers || []).map((trainer) => ({
          id: trainer._id,
          name: `${trainer.firstName} ${trainer.lastName}`,
          email: trainer.email,
          status: trainer.status || 'active',
          trainerStatus: trainer.trainerStatus || 'active',
          addOn: trainer.addOn || null,
          role: trainer.jobTitle || 'Trainer',
          expertise: trainer.expertise || [],
          joinedDate: trainer.createdAt?.split('T')[0] || '',
          lastActive: trainer.lastLogin || 'Never',
          sessionsCreated: trainer.sessionsCreated || 0,
          sessionsCompleted: trainer.sessionsCompleted || 0,
          totalParticipants: trainer.totalParticipants || 0,
          averageRating: trainer.averageRating || 0,
          totalRatings: trainer.totalRatings || 0,
          upcomingSessions: trainer.upcomingSessions || 0,
          bio: trainer.bio || '',
          phone: trainer.phone || '',
          certifications: trainer.certifications || [],
        }));
        setTrainers(transformedTrainers);

        // Update organization plan info
        if (response.data.limits) {
          setOrganizationPlan({
            plan: response.data.limits.plan || 'Free',
            trainersLimit: response.data.limits.trainersLimit ?? 1,
            trainersUsed: transformedTrainers.length,
            isUnlimited: response.data.limits.isUnlimited || response.data.limits.trainersLimit === null,
            activeAddOns: response.data.limits.activeAddOns || 0,
            trainerAddOns: response.data.limits.trainerAddOns || [],
          });
        }
      }
    } catch (error) {
      console.error('Error fetching trainers:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter trainers
  const filteredTrainers = trainers.filter((trainer) => {
    const matchesSearch =
      searchTerm === '' ||
      trainer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      trainer.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      trainer.expertise.some((exp) => exp.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesStatus = filterStatus === 'all' || trainer.trainerStatus === filterStatus;

    return matchesSearch && matchesStatus;
  });

  // Paginate filtered trainers
  const totalPages = Math.ceil(filteredTrainers.length / itemsPerPage);
  const paginatedTrainers = filteredTrainers.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Reset to page 1 when filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterStatus]);

  // Get stats
  const stats = {
    total: trainers.length,
    active: trainers.filter((t) => t.trainerStatus === 'active').length,
    totalSessions: trainers.reduce((sum, t) => sum + t.sessionsCreated, 0),
    avgRating: trainers.length > 0
      ? (trainers.reduce((sum, t) => sum + t.averageRating, 0) / trainers.length).toFixed(1)
      : 0,
  };

  const handleInviteTrainer = async () => {
    try {
      const nameParts = inviteName.trim().split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      const response = await clientService.inviteTrainer({
        email: inviteEmail,
        firstName,
        lastName,
      });

      if (response.success) {
        notifications.success('Invitation sent successfully!');
        setShowInviteModal(false);
        setInviteEmail('');
        setInviteName('');
        fetchTrainers(); // Refresh the list
      }
    } catch (error) {
      console.error('Error inviting trainer:', error);
      notifications.error(error.response?.data?.error || error.response?.data?.message || 'Failed to send invitation');
    }
  };

  const handleAddTrainer = async () => {
    try {
      // Validate that both first and last name are provided
      if (!newTrainerData.firstName.trim()) {
        notifications.error('Please enter first name');
        return;
      }
      if (!newTrainerData.lastName.trim()) {
        notifications.error('Please enter last name');
        return;
      }
      // Validate password match
      if (newTrainerData.password !== newTrainerData.confirmPassword) {
        notifications.error('Passwords do not match');
        return;
      }

      const response = await clientService.addTrainer({
        email: newTrainerData.email,
        firstName: newTrainerData.firstName.trim(),
        lastName: newTrainerData.lastName.trim(),
        phone: newTrainerData.phone,
        jobTitle: newTrainerData.role,
        status: newTrainerData.status,
        bio: newTrainerData.bio,
        expertise: newTrainerData.expertise,
        password: newTrainerData.password,
      });

      if (response.success) {
        notifications.success('Trainer added successfully!');
        setShowAddModal(false);
        setNewTrainerData({
          firstName: '',
          lastName: '',
          email: '',
          password: '',
          confirmPassword: '',
          phone: '',
          role: 'Trainer',
          status: 'active',
          bio: '',
          expertise: [],
          expertiseInput: '',
        });
        fetchTrainers(); // Refresh the list
      }
    } catch (error) {
      console.error('Error adding trainer:', error);
      notifications.error(error.response?.data?.error || error.response?.data?.message || 'Failed to add trainer');
    }
  };

  const handleAddExpertise = () => {
    if (newTrainerData.expertiseInput.trim()) {
      setNewTrainerData({
        ...newTrainerData,
        expertise: [...newTrainerData.expertise, newTrainerData.expertiseInput.trim()],
        expertiseInput: '',
      });
    }
  };

  const handleRemoveExpertise = (index) => {
    setNewTrainerData({
      ...newTrainerData,
      expertise: newTrainerData.expertise.filter((_, i) => i !== index),
    });
  };

  const handleActivateTrainer = async (trainer) => {
    try {
      const response = await clientService.activateTrainer(trainer.id);
      if (response.success) {
        notifications.success(`${trainer.name} has been activated`);
        fetchTrainers();
      } else {
        notifications.error(response.error || 'Failed to activate trainer');
      }
    } catch (error) {
      const msg = error.response?.data?.error || error.message || 'Failed to activate trainer';
      // If over limit, offer add-on purchase
      if (msg.includes('Trainer limit reached')) {
        notifications.error(`${msg} You can purchase an add-on to activate this trainer.`);
      } else {
        notifications.error(msg);
      }
    }
  };

  const handleDeactivateTrainer = async (trainer) => {
    try {
      const response = await clientService.deactivateTrainer(trainer.id);
      if (response.success) {
        notifications.success(`${trainer.name} has been deactivated`);
        fetchTrainers();
      } else {
        notifications.error(response.error || 'Failed to deactivate trainer');
      }
    } catch (error) {
      notifications.error(error.response?.data?.error || error.message || 'Failed to deactivate trainer');
    }
  };

  const handleReactivateAddon = async (trainer) => {
    try {
      setAddonLoading(true);
      const response = await clientService.purchaseTrainerAddon(trainer.id);
      if (response.success && response.data?.checkoutUrl) {
        window.location.href = response.data.checkoutUrl;
      } else {
        notifications.error(response.error || 'Failed to create checkout session');
      }
    } catch (error) {
      notifications.error(error.response?.data?.error || error.message || 'Failed to purchase add-on');
    } finally {
      setAddonLoading(false);
    }
  };

  const handleBuyAddonForTrainer = async (trainer) => {
    try {
      setAddonLoading(true);
      const response = await clientService.purchaseTrainerAddon(trainer.id);
      if (response.success && response.data?.checkoutUrl) {
        window.location.href = response.data.checkoutUrl;
      } else {
        notifications.error(response.error || 'Failed to create checkout session');
      }
    } catch (error) {
      notifications.error(error.response?.data?.error || error.message || 'Failed to purchase add-on');
    } finally {
      setAddonLoading(false);
    }
  };

  const handleViewDetails = (trainer) => {
    navigate(`/client/trainers/${trainer.id}`);
  };

  const handleRemoveTrainer = (trainer) => {
    setTrainerToDelete(trainer);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteTrainer = async () => {
    if (!trainerToDelete) return;

    try {
      const response = await clientService.removeTrainer(trainerToDelete.id);
      if (response.success) {
        notifications.success('Trainer removed successfully!');
        fetchTrainers(); // Refresh the list
      }
    } catch (error) {
      console.error('Error removing trainer:', error);
      notifications.error(error.response?.data?.error || error.response?.data?.message || 'Failed to remove trainer');
    } finally {
      setTrainerToDelete(null);
    }
  };

  const handleEditTrainer = (trainer) => {
    navigate(`/client/trainers/${trainer.id}/edit`);
  };

  const handleUpdateTrainer = async () => {
    try {
      // Validate that both first and last name are provided
      if (!editTrainerData.firstName.trim()) {
        notifications.error('Please enter first name');
        return;
      }
      if (!editTrainerData.lastName.trim()) {
        notifications.error('Please enter last name');
        return;
      }

      const response = await clientService.updateTrainer(editTrainerData.id, {
        firstName: editTrainerData.firstName.trim(),
        lastName: editTrainerData.lastName.trim(),
        phone: editTrainerData.phone,
        jobTitle: editTrainerData.role,
        expertise: editTrainerData.expertise,
        bio: editTrainerData.bio,
        status: editTrainerData.status,
      });

      if (response.success) {
        notifications.success('Trainer updated successfully!');
        setShowEditModal(false);
        fetchTrainers(); // Refresh the list
      }
    } catch (error) {
      console.error('Error updating trainer:', error);
      notifications.error(error.response?.data?.error || error.response?.data?.message || 'Failed to update trainer');
    }
  };

  const handleAddEditExpertise = () => {
    if (editTrainerData.expertiseInput.trim()) {
      setEditTrainerData({
        ...editTrainerData,
        expertise: [...editTrainerData.expertise, editTrainerData.expertiseInput.trim()],
        expertiseInput: '',
      });
    }
  };

  const handleRemoveEditExpertise = (index) => {
    setEditTrainerData({
      ...editTrainerData,
      expertise: editTrainerData.expertise.filter((_, i) => i !== index),
    });
  };

  // Generic addon purchase is no longer supported — add-ons are trainer-specific.
  // Use handleReactivateAddon or handleBuyAddonForTrainer instead.

  const isAtLimit = !organizationPlan.isUnlimited && organizationPlan.trainersUsed >= organizationPlan.trainersLimit;

  return (
    <DashboardLayout title="Trainers">
      <div className="p-4 space-y-4">
        {/* Back Button */}
        <div className="mb-4">
          <button
            onClick={() => navigate('/client/dashboard')}
            className="inline-flex items-center gap-2 text-neutral-600 hover:text-neutral-900 transition-colors mb-2 group"
          >
            <svg className="w-4 h-4 group-hover:-translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <span className="text-sm font-medium">Back to Dashboard</span>
          </button>
        </div>

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-0">
          <div>
            <h1 className="text-2xl font-bold text-neutral-900">Trainers</h1>
            <p className="text-sm text-neutral-600 mt-1 hidden sm:block">Manage your organization's trainers</p>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <Button variant="ghost" onClick={() => {
              if (!trainers.length) return;
              const headers = ['Name', 'Email', 'Phone', 'Status', 'Sessions Created', 'Sessions Completed', 'Avg Rating', 'Joined Date'];
              const rows = trainers.map(t => [
                t.name, t.email, t.phone || '', t.trainerStatus || t.status,
                t.sessionsCreated || 0, t.sessionsCompleted || 0,
                t.averageRating || 0, t.joinedDate || ''
              ]);
              const csv = [headers.join(','), ...rows.map(r => r.map(v => `"${v}"`).join(','))].join('\n');
              const blob = new Blob([csv], { type: 'text/csv' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `trainers_${new Date().toISOString().split('T')[0]}.csv`;
              a.click();
              URL.revokeObjectURL(url);
            }}>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
            </Button>
            <div>
              <Button
                variant="primary"
                onClick={() => navigate('/client/trainers/add')}
                disabled={isAtLimit}
              >
                <svg className="w-4 h-4 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                <span className="hidden sm:inline">Add Trainer</span>
              </Button>
            </div>
          </div>
        </div>

        {/* Plan Limit Warning */}
        {isAtLimit && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-yellow-50 border border-yellow-200 rounded-lg p-4"
          >
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-yellow-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div className="flex-1">
                <h3 className="font-semibold text-sm sm:text-base text-yellow-900 mb-1">Trainer Limit Reached</h3>
                <p className="text-xs sm:text-sm text-yellow-800">
                  You've reached your plan limit of {organizationPlan.trainersLimit} trainers.{' '}
                  <button onClick={() => navigate('/client/subscription')} className="underline hover:no-underline font-medium">
                    Upgrade your plan
                  </button> to invite more trainers.
                </p>
                {organizationPlan.plan !== 'free' && organizationPlan.plan !== 'enterprise_scale' && (
                  <p className="text-xs sm:text-sm text-yellow-800 mt-1">
                    Or purchase a trainer add-on (₹1,000 for 30 days) from the actions menu next to any inactive trainer below.
                  </p>
                )}
              </div>
            </div>
          </motion.div>
        )}

        {/* Inactive Trainers Warning - shows when trainers are over plan limit */}
        {trainers.some(t => t.trainerStatus === 'inactive') && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-orange-50 border border-orange-200 rounded-lg p-4"
          >
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-orange-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="flex-1">
                <h3 className="font-semibold text-sm text-orange-900 mb-1">
                  {trainers.filter(t => t.trainerStatus === 'inactive').length} trainer{trainers.filter(t => t.trainerStatus === 'inactive').length > 1 ? 's are' : ' is'} inactive
                </h3>
                <p className="text-xs text-orange-800">
                  Your plan allows {organizationPlan.isUnlimited ? 'unlimited' : organizationPlan.trainersLimit} trainer{organizationPlan.trainersLimit !== 1 ? 's' : ''}.
                  Inactive trainers can log in and view past data but cannot be assigned to new sessions.
                  Activate them, upgrade your plan, or purchase add-ons to reactivate.
                </p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
          <StatCard
            label="Total Trainers"
            value={organizationPlan.isUnlimited ? stats.total : `${stats.total} / ${organizationPlan.trainersLimit}`}
            icon={Users}
            variant="blue"
          />
          <StatCard label="Active Trainers" value={stats.active}        icon={CheckCircle2} variant="emerald" />
          <StatCard label="Total Sessions"  value={stats.totalSessions} icon={Calendar}     variant="violet" />
          <StatCard label="Avg Rating"      value={stats.avgRating}     icon={Star}         variant="amber" />
        </div>

        {/* Top Trainers Leaderboard - Top 10 */}
        {trainers.length > 0 && (
          <div className="bg-white rounded-2xl border border-neutral-100 overflow-hidden">
            <div className="p-5 border-b border-neutral-100">
              <h3 className="text-base font-bold text-neutral-900">Top Trainers Leaderboard</h3>
              <p className="text-xs text-neutral-500 mt-0.5">Based on Trainer Effectiveness (Q2)</p>
            </div>
            {/* Mobile Leaderboard Card View */}
            <div className="md:hidden divide-y divide-neutral-100">
              {[...trainers]
                .filter(t => t.trainerStatus === 'active')
                .sort((a, b) => (b.averageRating || 0) - (a.averageRating || 0))
                .slice(0, 10)
                .map((trainer, index) => (
                  <div key={trainer.id} className="p-4 flex items-center gap-3">
                    <span className="text-sm font-bold text-neutral-400 w-6 text-center flex-shrink-0">{index + 1}</span>
                    <div className="w-9 h-9 bg-primary-50 rounded-full flex items-center justify-center text-primary-600 font-semibold text-xs flex-shrink-0">
                      {trainer.name.split(' ').map(n => n[0]).join('')}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-neutral-900 truncate">{trainer.name}</p>
                      <div className="flex items-center gap-3 text-xs text-neutral-500">
                        <span>{trainer.sessionsCompleted} sessions</span>
                        <span className="text-amber-600 font-medium">TEI {trainer.averageRating?.toFixed(1) || '0.0'}</span>
                        <span>{trainer.totalParticipants} trained</span>
                      </div>
                    </div>
                  </div>
                ))}
            </div>

            {/* Desktop Leaderboard Table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full min-w-[800px]">
                <thead>
                  <tr className="border-b border-neutral-100">
                    <th className="text-left py-3.5 px-5 text-sm font-medium text-neutral-500">Rank</th>
                    <th className="text-left py-3.5 px-5 text-sm font-medium text-neutral-500">Trainer</th>
                    <th className="text-center py-3.5 px-5 text-sm font-medium text-neutral-500">Sessions</th>
                    <th className="text-center py-3.5 px-5 text-sm font-medium text-neutral-500">TEI (Q2)</th>
                    <th className="text-center py-3.5 px-5 text-sm font-medium text-neutral-500">Trained</th>
                  </tr>
                </thead>
                <tbody>
                  {[...trainers]
                    .filter(t => t.trainerStatus === 'active')
                    .sort((a, b) => (b.averageRating || 0) - (a.averageRating || 0))
                    .slice(0, 10)
                    .map((trainer, index) => (
                      <tr key={trainer.id} className="border-b border-neutral-50 hover:bg-neutral-50/60 transition-colors">
                        <td className="py-4 px-5">
                          <span className="text-sm font-medium text-neutral-700">{index + 1}</span>
                        </td>
                        <td className="py-4 px-5">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-primary-50 rounded-full flex items-center justify-center text-primary-600 font-semibold text-sm flex-shrink-0">
                              {trainer.name.split(' ').map(n => n[0]).join('')}
                            </div>
                            <div>
                              <p className="text-sm font-medium text-neutral-900">{trainer.name}</p>
                              <p className="text-xs text-neutral-400">{trainer.role}</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-4 px-5 text-center">
                          <span className="text-sm font-medium text-neutral-700">{trainer.sessionsCompleted}</span>
                        </td>
                        <td className="py-4 px-5 text-center">
                          <span className="text-sm font-medium text-amber-600">{trainer.averageRating?.toFixed(1) || '0.0'}</span>
                        </td>
                        <td className="py-4 px-5 text-center">
                          <span className="text-sm font-medium text-neutral-700">{trainer.totalParticipants}</span>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>

            {trainers.filter(t => t.trainerStatus === 'active').length === 0 && (
              <div className="text-center py-8">
                <p className="text-sm text-neutral-500">No active trainers to display</p>
              </div>
            )}
          </div>
        )}

        {/* Trainers Table Card */}
        <div className="bg-white rounded-2xl border border-neutral-100 overflow-hidden">
          {/* Search and Filters inside card */}
          <div className="p-5 border-b border-neutral-100">
            <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
              <div className="relative flex-1 w-full sm:max-w-md">
                <svg
                  className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  placeholder="Search by name, email, or expertise..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-10 py-2.5 text-sm border border-neutral-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all bg-neutral-50/50 placeholder:text-neutral-400"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
              <div className="flex items-center gap-3 w-full sm:w-auto">
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="flex-1 sm:flex-none px-3 py-2.5 text-sm border border-neutral-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 bg-white appearance-none pr-8 cursor-pointer text-neutral-700"
                  style={{ backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`, backgroundPosition: 'right 0.5rem center', backgroundRepeat: 'no-repeat', backgroundSize: '1.25em 1.25em' }}
                >
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>
          </div>

          {/* Trainers List */}
          {filteredTrainers.length === 0 ? (
            <div className="text-center py-16">
              <div className="inline-flex items-center justify-center w-14 h-14 bg-neutral-100 rounded-full mb-4">
                <svg className="w-6 h-6 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <p className="text-sm text-neutral-600 font-medium mb-1">No trainers found</p>
              <p className="text-xs text-neutral-400">Try adjusting your search or filters</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[800px]">
                  <thead>
                    <tr className="border-b border-neutral-100">
                      <th className="text-left py-3.5 px-5 text-sm font-medium text-neutral-500">Trainer</th>
                      <th className="text-left py-3.5 px-5 text-sm font-medium text-neutral-500">Role</th>
                      <th className="text-left py-3.5 px-5 text-sm font-medium text-neutral-500">Status</th>
                      <th className="text-left py-3.5 px-5 text-sm font-medium text-neutral-500">Sessions</th>
                      <th className="text-left py-3.5 px-5 text-sm font-medium text-neutral-500">Last Login</th>
                      <th className="text-right py-3.5 px-5 text-sm font-medium text-neutral-500">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedTrainers.map((trainer) => (
                      <motion.tr
                        key={trainer.id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="border-b border-neutral-50 hover:bg-neutral-50/60 transition-colors cursor-pointer"
                        onClick={() => handleViewDetails(trainer)}
                      >
                        <td className="py-4 px-5">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-primary-50 rounded-full flex items-center justify-center text-primary-600 font-semibold text-sm flex-shrink-0">
                              {trainer.name.split(' ').map(n => n[0]).join('')}
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-neutral-900 truncate">{trainer.name}</p>
                              <p className="text-xs text-neutral-400 truncate">{trainer.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-4 px-5">
                          <Badge variant="primary" size="sm">
                            {trainer.role}
                          </Badge>
                        </td>
                        <td className="py-4 px-5">
                          <div className="flex flex-col gap-1">
                            <Badge
                              variant={trainer.trainerStatus === 'active' ? 'live' : 'default'}
                              size="sm"
                            >
                              {trainer.trainerStatus === 'active' ? 'Active' : 'Inactive'}
                            </Badge>
                            {trainer.addOn?.status === 'active' && trainer.addOn?.expiresAt && (
                              <span className="text-[10px] text-blue-600">
                                Add-on: {Math.max(0, Math.ceil((new Date(trainer.addOn.expiresAt) - new Date()) / (1000 * 60 * 60 * 24)))}d left
                              </span>
                            )}
                            {trainer.addOn?.status === 'expired' && (
                              <span className="text-[10px] text-red-500">Add-on expired</span>
                            )}
                          </div>
                        </td>
                        <td className="py-4 px-5">
                          <span className="text-sm text-neutral-700">{trainer.sessionsCreated}</span>
                        </td>
                        <td className="py-4 px-5">
                          <span className="text-sm text-neutral-500">
                            {trainer.lastActive === 'Never'
                              ? 'Never'
                              : new Date(trainer.lastActive).toLocaleDateString('en-GB', {
                                  day: 'numeric',
                                  month: 'short',
                                  year: 'numeric',
                                })
                            }
                          </span>
                        </td>
                        <td className="py-4 px-5 text-right" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center justify-end gap-0.5">
                            {/* Activate/Deactivate/Reactivate button */}
                            {trainer.trainerStatus === 'inactive' && (
                              trainer.addOn?.status === 'expired' ? (
                                <button
                                  onClick={() => handleReactivateAddon(trainer)}
                                  disabled={addonLoading}
                                  className="px-2 py-1 rounded-lg text-xs font-medium bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors"
                                  title="Pay ₹1,000 to reactivate for 30 days"
                                >
                                  Reactivate
                                </button>
                              ) : (
                                <button
                                  onClick={() => handleActivateTrainer(trainer)}
                                  className="px-2 py-1 rounded-lg text-xs font-medium bg-green-50 text-green-700 hover:bg-green-100 transition-colors"
                                  title="Activate trainer"
                                >
                                  Activate
                                </button>
                              )
                            )}
                            {trainer.trainerStatus === 'active' && (
                              <button
                                onClick={() => handleDeactivateTrainer(trainer)}
                                className="px-2 py-1 rounded-lg text-xs font-medium bg-neutral-50 text-neutral-600 hover:bg-neutral-100 transition-colors"
                                title="Deactivate trainer"
                              >
                                Deactivate
                              </button>
                            )}
                            <button onClick={() => handleViewDetails(trainer)} className="p-2 rounded-lg text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 transition-colors" title="View">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                            </button>
                            <button onClick={() => handleEditTrainer(trainer)} className="p-2 rounded-lg text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 transition-colors" title="Edit">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                            <button onClick={() => handleRemoveTrainer(trainer)} className="p-2 rounded-lg text-neutral-400 hover:text-red-600 hover:bg-red-50 transition-colors" title="Remove">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
          {/* Pagination */}
          {filteredTrainers.length > 0 && (
            <div className="px-5 py-4 border-t border-neutral-100">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                itemsPerPage={itemsPerPage}
                totalItems={filteredTrainers.length}
              />
            </div>
          )}
        </div>

        {/* Add Trainer Modal */}
        <Modal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          title="Add Trainer"
          size="md"
        >
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  First Name *
                </label>
                <Input
                  placeholder="John"
                  value={newTrainerData.firstName}
                  onChange={(e) => setNewTrainerData({ ...newTrainerData, firstName: e.target.value })}
                  fullWidth
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Last Name *
                </label>
                <Input
                  placeholder="Doe"
                  value={newTrainerData.lastName}
                  onChange={(e) => setNewTrainerData({ ...newTrainerData, lastName: e.target.value })}
                  fullWidth
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Email Address *
              </label>
              <Input
                type="email"
                placeholder="trainer@example.com"
                value={newTrainerData.email}
                onChange={(e) => setNewTrainerData({ ...newTrainerData, email: e.target.value })}
                fullWidth
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Password *
              </label>
              <Input
                type="password"
                placeholder="Min. 8 characters"
                value={newTrainerData.password}
                onChange={(e) => setNewTrainerData({ ...newTrainerData, password: e.target.value })}
                fullWidth
              />
              {newTrainerData.password && newTrainerData.password.length < 8 ? (
                <p className="text-xs text-red-500 mt-1">Password must be at least 8 characters</p>
              ) : (
                <p className="text-xs text-neutral-500 mt-1">
                  The trainer will use this password to login for the first time.
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Confirm Password *
              </label>
              <Input
                type="password"
                placeholder="Re-enter password"
                value={newTrainerData.confirmPassword}
                onChange={(e) => setNewTrainerData({ ...newTrainerData, confirmPassword: e.target.value })}
                fullWidth
              />
              {newTrainerData.password && newTrainerData.confirmPassword && newTrainerData.password !== newTrainerData.confirmPassword && (
                <p className="text-xs text-red-500 mt-1">
                  Passwords do not match
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Phone Number
              </label>
              <Input
                type="tel"
                placeholder="+1 (555) 123-4567"
                value={newTrainerData.phone}
                onChange={(e) => setNewTrainerData({ ...newTrainerData, phone: e.target.value })}
                fullWidth
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Role
              </label>
              <select
                value={newTrainerData.role}
                onChange={(e) => setNewTrainerData({ ...newTrainerData, role: e.target.value })}
                className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="Trainer">Trainer</option>
                <option value="Senior Trainer">Senior Trainer</option>
                <option value="Lead Trainer">Lead Trainer</option>
                <option value="Training Manager">Training Manager</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Status
              </label>
              <select
                value={newTrainerData.status}
                onChange={(e) => setNewTrainerData({ ...newTrainerData, status: e.target.value })}
                className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Bio
              </label>
              <textarea
                placeholder="Brief description about the trainer..."
                value={newTrainerData.bio}
                onChange={(e) => setNewTrainerData({ ...newTrainerData, bio: e.target.value })}
                rows={3}
                className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Areas of Expertise
              </label>
              <div className="flex gap-2 mb-2">
                <Input
                  placeholder="e.g., Leadership, Sales"
                  value={newTrainerData.expertiseInput}
                  onChange={(e) => setNewTrainerData({ ...newTrainerData, expertiseInput: e.target.value })}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddExpertise();
                    }
                  }}
                  fullWidth
                />
                <Button variant="secondary" onClick={handleAddExpertise}>
                  Add
                </Button>
              </div>
              {newTrainerData.expertise.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {newTrainerData.expertise.map((exp, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-primary-50 text-primary-700 rounded-full text-sm font-medium flex items-center gap-2"
                    >
                      {exp}
                      <button
                        onClick={() => handleRemoveExpertise(index)}
                        className="text-primary-700 hover:text-primary-900"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <p className="text-sm text-green-800">
                The trainer will be added immediately with full access to create and manage sessions.
              </p>
            </div>
            <div className="flex gap-3 pt-4">
              <Button
                variant="primary"
                fullWidth
                onClick={handleAddTrainer}
                disabled={!newTrainerData.firstName || !newTrainerData.lastName || !newTrainerData.email || !newTrainerData.password || newTrainerData.password.length < 8 || newTrainerData.password !== newTrainerData.confirmPassword}
              >
                Add Trainer
              </Button>
              <Button
                variant="ghost"
                fullWidth
                onClick={() => setShowAddModal(false)}
              >
                Cancel
              </Button>
            </div>
          </div>
        </Modal>

        {/* Invite Trainer Modal */}
        <Modal
          isOpen={showInviteModal}
          onClose={() => setShowInviteModal(false)}
          title="Invite Trainer"
        >
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Trainer Name
              </label>
              <Input
                placeholder="Enter trainer's name"
                value={inviteName}
                onChange={(e) => setInviteName(e.target.value)}
                fullWidth
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Email Address
              </label>
              <Input
                type="email"
                placeholder="trainer@example.com"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                fullWidth
              />
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-800">
                An invitation email will be sent to the trainer with instructions to join your organization.
              </p>
            </div>
            <div className="flex gap-3 pt-4">
              <Button
                variant="primary"
                fullWidth
                onClick={handleInviteTrainer}
                disabled={!inviteName || !inviteEmail}
              >
                Send Invitation
              </Button>
              <Button
                variant="ghost"
                fullWidth
                onClick={() => setShowInviteModal(false)}
              >
                Cancel
              </Button>
            </div>
          </div>
        </Modal>

        {/* Trainer Details Modal */}
        {selectedTrainer && (
          <Modal
            isOpen={showDetailsModal}
            onClose={() => setShowDetailsModal(false)}
            title="Trainer Profile"
          >
            <div className="space-y-6">
              {/* Header */}
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 bg-gradient-to-br from-primary-500 to-primary-600 rounded-full flex items-center justify-center text-white font-bold text-2xl">
                  {selectedTrainer.name.split(' ').map(n => n[0]).join('')}
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-neutral-900">{selectedTrainer.name}</h3>
                  <p className="text-neutral-600">{selectedTrainer.role}</p>
                  <Badge variant={selectedTrainer.status === 'active' ? 'live' : 'default'} size="sm" className="mt-2">
                    {selectedTrainer.status}
                  </Badge>
                </div>
              </div>

              {/* Bio */}
              <div>
                <h4 className="font-semibold text-neutral-900 mb-2">About</h4>
                <p className="text-sm text-neutral-600">{selectedTrainer.bio}</p>
              </div>

              {/* Contact */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="font-semibold text-neutral-900 mb-2 text-sm">Email</h4>
                  <p className="text-sm text-neutral-600">{selectedTrainer.email}</p>
                </div>
                <div>
                  <h4 className="font-semibold text-neutral-900 mb-2 text-sm">Phone</h4>
                  <p className="text-sm text-neutral-600">{selectedTrainer.phone}</p>
                </div>
              </div>

              {/* Expertise */}
              <div>
                <h4 className="font-semibold text-neutral-900 mb-2">Expertise</h4>
                {selectedTrainer.expertise && selectedTrainer.expertise.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {selectedTrainer.expertise.map((exp, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-primary-50 text-primary-700 rounded-full text-sm font-medium"
                      >
                        {exp}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-neutral-500 italic">No expertise areas added yet</p>
                )}
              </div>

              {/* Certifications */}
              <div>
                <h4 className="font-semibold text-neutral-900 mb-2">Certifications</h4>
                {selectedTrainer.certifications && selectedTrainer.certifications.length > 0 ? (
                  <ul className="space-y-1">
                    {selectedTrainer.certifications.map((cert, index) => (
                      <li key={index} className="flex items-center gap-2 text-sm text-neutral-600">
                        <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        {cert}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-neutral-500 italic">No certifications added yet</p>
                )}
              </div>

              {/* Performance Stats */}
              <div className="grid grid-cols-3 gap-4 pt-4 border-t border-neutral-200">
                <div>
                  <p className="text-xs text-neutral-500 mb-1">Sessions Created</p>
                  <p className="text-2xl font-bold text-neutral-900">{selectedTrainer.sessionsCreated}</p>
                </div>
                <div>
                  <p className="text-xs text-neutral-500 mb-1">Sessions Completed</p>
                  <p className="text-2xl font-bold text-green-600">{selectedTrainer.sessionsCompleted}</p>
                </div>
                <div>
                  <p className="text-xs text-neutral-500 mb-1">Avg. Feedback Rating</p>
                  <div className="flex items-center gap-1">
                    <p className="text-2xl font-bold text-purple-600">{selectedTrainer.averageRating?.toFixed(1) || '0.0'}</p>
                    <svg className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t border-neutral-200">
                <Button variant="primary" fullWidth onClick={() => {
                  setShowDetailsModal(false);
                  handleEditTrainer(selectedTrainer);
                }}>
                  Edit Trainer
                </Button>
                <Button variant="ghost" fullWidth onClick={() => setShowDetailsModal(false)}>
                  Close
                </Button>
              </div>
            </div>
          </Modal>
        )}

        {/* Edit Trainer Modal */}
        <Modal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          title="Edit Trainer"
          size="md"
        >
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  First Name *
                </label>
                <Input
                  placeholder="John"
                  value={editTrainerData.firstName}
                  onChange={(e) => setEditTrainerData({ ...editTrainerData, firstName: e.target.value })}
                  fullWidth
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  Last Name *
                </label>
                <Input
                  placeholder="Doe"
                  value={editTrainerData.lastName}
                  onChange={(e) => setEditTrainerData({ ...editTrainerData, lastName: e.target.value })}
                  fullWidth
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Email Address
              </label>
              <Input
                type="email"
                value={editTrainerData.email}
                disabled
                fullWidth
                className="bg-neutral-100"
              />
              <p className="text-xs text-neutral-500 mt-1">Email cannot be changed</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Phone Number
              </label>
              <Input
                type="tel"
                placeholder="+1 (555) 123-4567"
                value={editTrainerData.phone}
                onChange={(e) => setEditTrainerData({ ...editTrainerData, phone: e.target.value })}
                fullWidth
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Role
              </label>
              <select
                value={editTrainerData.role}
                onChange={(e) => setEditTrainerData({ ...editTrainerData, role: e.target.value })}
                className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="Trainer">Trainer</option>
                <option value="Senior Trainer">Senior Trainer</option>
                <option value="Lead Trainer">Lead Trainer</option>
                <option value="Training Manager">Training Manager</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Status
              </label>
              <select
                value={editTrainerData.status}
                onChange={(e) => setEditTrainerData({ ...editTrainerData, status: e.target.value })}
                className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Bio
              </label>
              <textarea
                placeholder="Brief description about the trainer..."
                value={editTrainerData.bio}
                onChange={(e) => setEditTrainerData({ ...editTrainerData, bio: e.target.value })}
                rows={3}
                className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Areas of Expertise
              </label>
              <div className="flex gap-2 mb-2">
                <Input
                  placeholder="e.g., Leadership, Sales"
                  value={editTrainerData.expertiseInput}
                  onChange={(e) => setEditTrainerData({ ...editTrainerData, expertiseInput: e.target.value })}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddEditExpertise();
                    }
                  }}
                  fullWidth
                />
                <Button variant="secondary" onClick={handleAddEditExpertise}>
                  Add
                </Button>
              </div>
              {editTrainerData.expertise.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {editTrainerData.expertise.map((exp, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-primary-50 text-primary-700 rounded-full text-sm font-medium flex items-center gap-2"
                    >
                      {exp}
                      <button
                        onClick={() => handleRemoveEditExpertise(index)}
                        className="text-primary-700 hover:text-primary-900"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div className="flex gap-3 pt-4">
              <Button
                variant="primary"
                fullWidth
                onClick={handleUpdateTrainer}
                disabled={!editTrainerData.firstName || !editTrainerData.lastName}
              >
                Save Changes
              </Button>
              <Button
                variant="ghost"
                fullWidth
                onClick={() => setShowEditModal(false)}
              >
                Cancel
              </Button>
            </div>
          </div>
        </Modal>

        {/* Delete Confirmation Dialog */}
        <ConfirmDialog
          isOpen={showDeleteConfirm}
          onClose={() => {
            setShowDeleteConfirm(false);
            setTrainerToDelete(null);
          }}
          onConfirm={confirmDeleteTrainer}
          title="Remove Trainer?"
          message={trainerToDelete ? `Are you sure you want to remove ${trainerToDelete.name} from your organization? This action cannot be undone.` : ''}
          confirmText="Remove"
          cancelText="Cancel"
          variant="danger"
        />
      </div>
    </DashboardLayout>
  );
};

export default TrainersPage;